// ====================================================================================================
//  File Name   : ThreatMapWidget.js
//  Location    : frontend/src/features/dashboard/ThreatMapWidget.js
//  Project     : AIRS Security - Autonomous Sentinel Ecosystem
//  Developed By: Appence Software Solution | https://appence.com
//  Legal Notice: Proprietary Property. Unauthorized use strictly prohibited. (c) 2026
// ====================================================================================================

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import Globe from 'react-globe.gl';
import { 
  Globe as GlobeIcon, 
  Maximize, 
  Minimize, 
  ShieldCheck, 
  ShieldAlert, 
  Crosshair, 
  Activity, 
  Radio, 
  Play, 
  Pause,
  MapPin,
  ZoomIn,
  ZoomOut
} from 'lucide-react';

const ThreatMapWidget = () => {
  const globeRef = useRef();
  const containerRef = useRef();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 1100, height: 650 });
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [hoveredEntity, setHoveredEntity] = useState(null);
  const [isRotating, setIsRotating] = useState(true);

  // 1. Fetch Real-time Telemetry Data
  const { data = { agents: [], threats: [] }, isLoading } = useQuery({
    queryKey: ['globalMapData'],
    queryFn: async () => {
      try {
        const res = await axiosClient.get('/admin/stats/threat-map');
        if (res && res.agents) return res;
        if (res && res.data && res.data.agents) return res.data;
        return { agents: [], threats: [] };
      } catch {
        return { agents: [], threats: [] };
      }
    },
    refetchInterval: 5000,
    staleTime: 4000
  });

  // 2. Responsive Canvas Dimensions with Minimum Safe Boundaries
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth || containerRef.current.offsetWidth || window.innerWidth - 320;
        const h = isFullscreen ? window.innerHeight : 650;
        setDimensions({
          width: Math.max(w, 800),
          height: h
        });
      }
    };

    updateDimensions();
    const timer = setTimeout(updateDimensions, 200);

    window.addEventListener('resize', updateDimensions);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateDimensions);
    };
  }, [isFullscreen]);

  // 3. Globe Auto-Rotation & Camera Controls
  useEffect(() => {
    if (globeRef.current) {
      globeRef.current.controls().autoRotate = isRotating;
      globeRef.current.controls().autoRotateSpeed = 0.5;
      globeRef.current.controls().enableZoom = true;
      globeRef.current.pointOfView({ lat: 20.5937, lng: 78.9629, altitude: 2.1 }, 600);
    }
  }, [isRotating]);

  // 4. Clustered Agent Nodes
  const clusteredAgentPoints = useMemo(() => {
    const locMap = {};
    (data.agents || []).forEach((agent, idx) => {
      const rawLat = Number(agent.latitude || 20.5937);
      const rawLng = Number(agent.longitude || 78.9629);
      const key = `${rawLat.toFixed(2)}_${rawLng.toFixed(2)}`;

      if (!locMap[key]) {
        locMap[key] = [];
      }
      locMap[key].push({ ...agent, originalIdx: idx });
    });

    const points = [];
    Object.keys(locMap).forEach((key) => {
      const cluster = locMap[key];
      const count = cluster.length;

      cluster.forEach((agent, i) => {
        let lat = Number(agent.latitude || 20.5937);
        let lng = Number(agent.longitude || 78.9629);

        if (count > 1) {
          const angle = (i / count) * 2 * Math.PI;
          const radius = 0.7;
          lat += Math.sin(angle) * radius;
          lng += Math.cos(angle) * radius;
        }

        points.push({
          lat,
          lng,
          size: 0.35,
          color: '#10b981',
          name: agent.hostname || 'Active Endpoint',
          ip: agent.ip_address || 'Protected',
          location: `${agent.city || 'Secure'}, ${agent.country || 'Node'}`,
          clusterCount: count,
          clusterIndex: i + 1,
          type: 'agent'
        });
      });
    });

    return points;
  }, [data.agents]);

  // 5. Threat Neutralization Radar Waves
  const threatRings = useMemo(() => {
    return (data.threats || []).map((t) => ({
      lat: Number(t.lat || 20.5937),
      lng: Number(t.lng || 78.9629),
      maxR: 3.5,
      propagationSpeed: 1.8,
      repeatPeriod: 1200,
      color: '#ef4444'
    }));
  }, [data.threats]);

  // 6. Laser Trajectory Attack Arcs
  const attackArcs = useMemo(() => {
    return (data.threats || []).map((t, idx) => {
      const targetAgent = (data.agents || [])[idx % (data.agents?.length || 1)] || { latitude: 20.5937, longitude: 78.9629 };
      return {
        startLat: Number(t.src_lat || t.lat || (Math.random() * 100 - 50)),
        startLng: Number(t.src_lng || t.lng || (Math.random() * 320 - 160)),
        endLat: Number(targetAgent.latitude || 20.5937),
        endLng: Number(targetAgent.longitude || 78.9629),
        color: ['rgba(239, 68, 68, 0.9)', 'rgba(245, 158, 11, 0.7)', 'rgba(16, 185, 129, 0.95)'],
        stroke: 0.18,
        threatType: t.type || 'Mitigated Exploit Vector',
        targetName: targetAgent.hostname || 'Protected Node'
      };
    });
  }, [data.threats, data.agents]);

  // Regional Camera Navigation
  const focusRegion = (lat, lng, alt = 1.3) => {
    if (globeRef.current) {
      globeRef.current.pointOfView({ lat, lng, altitude: alt }, 1000);
    }
  };

  const adjustZoom = (delta) => {
    if (globeRef.current) {
      const currentPov = globeRef.current.pointOfView();
      const newAlt = Math.max(0.5, Math.min(3.2, (currentPov.altitude || 2.1) + delta));
      globeRef.current.pointOfView({ ...currentPov, altitude: newAlt }, 400);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => alert(err.message));
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  return (
    <div 
      ref={containerRef} 
      className={`bg-[#020617] border border-cyan-950/60 rounded-2xl shadow-2xl overflow-hidden flex flex-col transition-all duration-300 relative ${
        isFullscreen ? 'fixed inset-0 z-50 h-screen w-screen rounded-none' : 'h-[650px] w-full'
      }`}
    >
      {/* HUD Header Bar */}
      <div className="p-4 border-b border-cyan-950/40 bg-[#020617]/90 backdrop-blur-md flex justify-between items-center z-10 absolute top-0 left-0 right-0 pointer-events-auto">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.25)]">
            <GlobeIcon className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <h3 className="font-bold text-slate-100 text-sm flex items-center gap-2 tracking-wide font-mono">
              AIRS SOVEREIGN 3D DEFENSE GRID
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 uppercase font-mono">
                Real-Time 3D
              </span>
            </h3>
            <p className="text-[10px] text-cyan-500/70 font-mono tracking-wider flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span> Live Endpoint Vector Topology (Hover node for telemetry)
            </p>
          </div>
        </div>
         
        <div className="flex items-center gap-2.5">
          {/* Quick Focus Controls */}
          <div className="hidden lg:flex items-center gap-1 bg-slate-950/90 px-2 py-1 rounded-lg border border-slate-800 text-[10px] font-mono">
            <span className="text-slate-400 mr-1 flex items-center gap-1"><MapPin className="w-3 h-3 text-cyan-400"/> Focus:</span>
            <button 
              onClick={() => focusRegion(20.5937, 78.9629, 1.2)} 
              className="px-2.5 py-1 rounded bg-slate-900 hover:bg-cyan-950 text-cyan-300 border border-cyan-800/40 hover:border-cyan-500 transition-all"
            >
              🇮🇳 India
            </button>
            <button 
              onClick={() => focusRegion(51.5074, 10.1278, 1.3)} 
              className="px-2.5 py-1 rounded bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-800 transition-all"
            >
              🇪🇺 EMEA
            </button>
            <button 
              onClick={() => focusRegion(38.8951, -97.0364, 1.4)} 
              className="px-2.5 py-1 rounded bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-800 transition-all"
            >
              🇺🇸 US
            </button>
            <button 
              onClick={() => focusRegion(20, 0, 2.3)} 
              className="px-2.5 py-1 rounded bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-800 transition-all"
            >
              🌐 Global
            </button>
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center bg-slate-950/90 rounded-lg border border-slate-800 p-0.5">
            <button 
              onClick={() => adjustZoom(-0.35)} 
              className="p-1.5 hover:bg-slate-800 text-slate-300 hover:text-cyan-300 rounded" 
              title="Zoom In"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button 
              onClick={() => adjustZoom(0.35)} 
              className="p-1.5 hover:bg-slate-800 text-slate-300 hover:text-cyan-300 rounded" 
              title="Zoom Out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
          </div>

          {/* Auto-Rotation Toggle */}
          <button 
            onClick={() => setIsRotating(!isRotating)} 
            className={`p-1.5 rounded-lg border transition-all ${
              isRotating 
                ? 'bg-cyan-950/80 border-cyan-500/50 text-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.3)]' 
                : 'bg-slate-900 border-slate-800 text-slate-400'
            }`}
            title={isRotating ? "Pause Orbit" : "Resume Orbit"}
          >
            {isRotating ? <Pause className="w-4 h-4"/> : <Play className="w-4 h-4"/>}
          </button>

          {/* Fullscreen Button */}
          <button 
            onClick={toggleFullscreen} 
            className="p-1.5 bg-slate-900 hover:bg-cyan-950 text-slate-300 hover:text-cyan-300 border border-slate-800 hover:border-cyan-700/50 rounded-lg transition-all"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen View"}
          >
            {isFullscreen ? <Minimize className="w-4 h-4"/> : <Maximize className="w-4 h-4"/>}
          </button>
        </div>
      </div>

      {/* 3D WebGL Globe Viewport */}
      <div className="flex-1 w-full h-full relative cursor-grab active:cursor-grabbing bg-[#020617] flex items-center justify-center">
        {isLoading ? (
          <div className="flex items-center justify-center h-full text-cyan-500 font-mono text-xs">
            <Activity className="w-5 h-5 animate-spin mr-2"/> Calibrating Sovereign Orbital Grid...
          </div>
        ) : (
          <Globe
            ref={globeRef}
            width={dimensions.width}
            height={dimensions.height}
            
            // Ultra-Reliable Cyber Mesh Shaders (Zero Blank Screen / Zero CORS)
            globeImageUrl="https://unpkg.com/three-globe/example/img/earth-night.jpg"
            bumpImageUrl="https://unpkg.com/three-globe/example/img/earth-topology.png"
            backgroundImageUrl="https://unpkg.com/three-globe/example/img/night-sky.png"
            atmosphereColor="#06b6d4"
            atmosphereAltitude={0.22}
            
            // 3D Endpoint Beacons
            pointsData={clusteredAgentPoints}
            pointColor="color"
            pointAltitude={0.03}
            pointRadius={0.45}
            pointsMerge={false}
            onPointHover={(pt) => setHoveredEntity(pt)}
            onPointClick={(pt) => setSelectedEntity(pt)}
            pointLabel={d => `
              <div style="background: rgba(2, 6, 23, 0.95); border: 1px solid #10b981; padding: 8px 12px; border-radius: 8px; font-family: monospace; color: #f8fafc; box-shadow: 0 0 15px rgba(16, 185, 129, 0.4);">
                <div style="font-weight: bold; color: #34d399; display: flex; align-items: center; gap: 4px;">🛡️ ${d.name} ${d.clusterCount > 1 ? `(Node ${d.clusterIndex}/${d.clusterCount})` : ''}</div>
                <div style="font-size: 11px; color: #94a3b8; margin-top: 2px;">📍 ${d.location}</div>
                <div style="font-size: 10px; color: #6ee7b7; margin-top: 2px;">IP: ${d.ip} | Status: Online (eBPF Active)</div>
              </div>
            `}

            // Threat Neutralization Wave Rings
            ringsData={threatRings}
            ringColor={() => (t) => `rgba(239, 68, 68, ${Math.max(0, 0.85 * (1 - t))})`}
            ringMaxRadius="maxR"
            ringPropagationSpeed="propagationSpeed"
            ringRepeatPeriod="repeatPeriod"

            // Precision Laser Trajectory Arcs
            arcsData={attackArcs}
            arcColor="color"
            arcDashLength={0.25}
            arcDashGap={0.75}
            arcDashAnimateTime={2000}
            arcStroke={0.18}
            arcAltitude={0.22}
            arcLabel={d => `
              <div style="background: rgba(2, 6, 23, 0.95); border: 1px solid #ef4444; padding: 8px 12px; border-radius: 8px; font-family: monospace; color: #f8fafc; box-shadow: 0 0 15px rgba(239, 68, 68, 0.4);">
                <div style="font-weight: bold; color: #f87171;">⚡ MITRE Attack Vector Neutralized</div>
                <div style="font-size: 11px; color: #cbd5e1;">Target Node: ${d.targetName}</div>
                <div style="font-size: 10px; color: #ef4444; margin-top: 2px;">Vector: ${d.threatType} (Autonomous Containment)</div>
              </div>
            `}
          />
        )}

        {/* 🧭 TACTICAL HUD EXPLANATORY KEY (Bottom-Left) */}
        <div className="absolute bottom-4 left-4 z-10 pointer-events-none hidden md:block">
          <div className="bg-slate-950/90 backdrop-blur-md border border-cyan-900/50 p-3.5 rounded-xl font-mono text-[11px] text-slate-300 space-y-2 shadow-[0_0_20px_rgba(0,0,0,0.9)] max-w-xs">
            <div className="text-cyan-400 font-bold flex items-center gap-1.5 border-b border-cyan-900/40 pb-1 text-xs">
              <Crosshair className="w-3.5 h-3.5 text-cyan-400" /> TACTICAL DEFENSE KEY
            </div>

            <div className="flex items-center gap-2 text-[10px]">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.9)] shrink-0"></span>
              <div>
                <span className="text-emerald-300 font-bold">Green Beacon:</span>
                <span className="text-slate-400 ml-1">Active Sentinel Node</span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-[10px]">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-ping shadow-[0_0_8px_rgba(239,68,68,0.9)] shrink-0"></span>
              <div>
                <span className="text-rose-400 font-bold">Red Ripple Ring:</span>
                <span className="text-slate-400 ml-1">Threat Blocked by eBPF</span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-[10px]">
              <span className="w-3 h-0.5 bg-gradient-to-r from-rose-500 via-amber-400 to-emerald-400 shrink-0"></span>
              <div>
                <span className="text-amber-300 font-bold">Laser Beam:</span>
                <span className="text-slate-400 ml-1">Inbound Attack Trajectory</span>
              </div>
            </div>

            <div className="pt-1 border-t border-slate-900 flex justify-between text-[9px] text-cyan-500/70">
              <span>CANVAS: 650PX EXPANDED</span>
              <span>DEFENSE: TITANIUM AI</span>
            </div>
          </div>
        </div>

        {/* ⚡ LIVE THREAT INTERCEPTION FEED TICKER (Bottom-Right) */}
        <div className="absolute bottom-4 right-4 z-10 pointer-events-none hidden lg:block">
          <div className="bg-slate-950/90 backdrop-blur-md border border-slate-800/80 p-3 rounded-xl font-mono text-[10px] text-slate-300 shadow-[0_0_20px_rgba(0,0,0,0.9)] max-w-xs space-y-1.5">
            <div className="text-rose-400 font-bold flex items-center gap-1.5 border-b border-slate-800 pb-1">
              <ShieldAlert className="w-3.5 h-3.5 text-rose-400 animate-pulse" /> LIVE INTERCEPTION FEED
            </div>
            <div className="space-y-1">
              {(data.threats || []).slice(0, 3).map((t, idx) => (
                <div key={`feed-${idx}`} className="flex items-center justify-between text-[9px] bg-slate-900/60 px-2 py-1 rounded border border-slate-800/50">
                  <span className="text-rose-400 font-bold truncate max-w-[120px]">{t.type || 'Exploit Blocked'}</span>
                  <span className="text-emerald-400">Neutralized ✓</span>
                </div>
              ))}
              {(!data.threats || data.threats.length === 0) && (
                <div className="text-slate-500 text-[9px] italic">No active anomalous intrusions detected.</div>
              )}
            </div>
          </div>
        </div>

        {/* Dynamic Hover/Click Telemetry Card */}
        {(selectedEntity || hoveredEntity) && (
          <div className="absolute top-20 right-4 z-20 bg-slate-950/95 border border-emerald-500/60 p-4 rounded-xl font-mono text-xs text-slate-200 shadow-[0_0_25px_rgba(16,185,129,0.25)] max-w-sm pointer-events-auto">
            <div className="flex justify-between items-start mb-2">
              <div className="font-bold text-emerald-400 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-400" /> {(selectedEntity || hoveredEntity).name}
              </div>
              {selectedEntity && (
                <button 
                  onClick={() => setSelectedEntity(null)} 
                  className="text-slate-500 hover:text-slate-300 text-sm leading-none"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="text-[11px] text-slate-400">📍 {(selectedEntity || hoveredEntity).location}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">IP Address: {(selectedEntity || hoveredEntity).ip}</div>
            <div className="mt-2 text-[10px] text-emerald-400 bg-emerald-950/40 border border-emerald-800/40 px-2 py-1 rounded flex items-center gap-1.5">
              <Radio className="w-3 h-3 animate-pulse" /> Sentinel Kernel Protection Active
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThreatMapWidget;
